import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const passwordValid = password.length >= 8;
  const matches = password === confirm && password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid || !matches) return;
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      toast.success('Password reset successfully');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || 'Invalid request')
        : (typeof detail === 'string' ? detail : 'Could not reset password');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-sand relative overflow-hidden flex items-center justify-center p-6" data-testid="reset-password-no-token">
        <div className="noise-overlay" />
        <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
        <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />
        <div className="max-w-md text-center animate-fade-up relative z-10">
          <AlertCircle size={48} className="text-gold mx-auto mb-5" />
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-dark mb-4 mystical-heading">Invalid reset link</h1>
          <p className="font-sans text-base text-text-muted leading-relaxed mb-8">
            This link is missing a reset token. Please request a new one.
          </p>
          <Link
            to="/forgot-password"
            data-testid="request-new-link"
            className="inline-flex items-center justify-center h-12 px-8 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider uppercase btn-hover"
          >
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="reset-password-page">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          {!done ? (
            <>
              <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Fresh Start</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight leading-tight mystical-heading shimmer-text">
                Choose a new password
              </h1>
              <p className="text-text-muted font-sans text-base mt-4 leading-relaxed">
                Pick something memorable — at least 6 characters.
              </p>
              <div className="mystical-divider mt-6 mb-8" />

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-text-muted text-xs tracking-wider uppercase font-sans">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="new-password-input"
                      placeholder="Enter new password"
                      required
                      className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-dark"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {password && !passwordValid && (
                    <p className="text-xs text-red-500/80 font-sans">At least 8 characters</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-text-muted text-xs tracking-wider uppercase font-sans">Confirm Password</Label>
                  <Input
                    id="confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="confirm-password-input"
                    placeholder="Confirm new password"
                    required
                    className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
                  />
                  {confirm && !matches && (
                    <p className="text-xs text-red-500/80 font-sans">Passwords don't match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={submitting || !passwordValid || !matches}
                  className="w-full h-12 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider uppercase btn-hover disabled:opacity-50"
                >
                  {submitting ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            </>
          ) : (
            <div className="mystical-card p-10 text-center" data-testid="reset-success">
              <div className="relative w-20 h-20 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
                <div className="absolute inset-3 rounded-full bg-gold/20 animate-breathe flex items-center justify-center" style={{ animationDelay: '0.5s' }}>
                  <CheckCircle2 size={24} className="text-gold" />
                </div>
              </div>
              <h3 className="font-serif text-3xl text-dark mb-3">Password reset</h3>
              <p className="font-sans text-sm text-text-muted leading-relaxed mb-8">
                Your password has been updated. Sign in with your new password to continue.
              </p>
              <Button
                onClick={() => navigate('/login', { replace: true })}
                data-testid="go-to-login-button"
                className="w-full h-12 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider uppercase btn-hover"
              >
                Sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
